import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Bell,
  Coins,
  Globe2,
  Newspaper,
  Server,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';

interface KpiNumber {
  readonly value: number;
  readonly previous: number | null;
  readonly deltaPct: number | null;
}

interface OverviewResponse {
  readonly kpis: {
    readonly dau: KpiNumber | null;
    readonly mau: KpiNumber | null;
    readonly articlesToday: KpiNumber;
    readonly pushSentToday: KpiNumber;
  };
  readonly secondary: {
    readonly newUsers24h: KpiNumber;
    readonly activeChannels: KpiNumber;
    readonly errors24h: KpiNumber | null;
    readonly mtdSpend: KpiNumber | null;
  };
  readonly totals: {
    readonly users: number;
    readonly articles: number;
    readonly channels: number;
    readonly activeAlerts: number;
    readonly devices: number;
  };
  readonly computedAt: string;
}

const fmtInt = new Intl.NumberFormat('en-US');

function formatValue(v: KpiNumber | null): string {
  if (!v) return '—';
  return fmtInt.format(v.value);
}

function DeltaBadge({ delta }: { readonly delta: number | null }) {
  if (delta === null) {
    return <span className="nw-kpi__delta-note">no comparison</span>;
  }
  if (delta === 0) return <span className="nw-kpi__delta-note">no change</span>;
  const up = delta > 0;
  const Cls = up ? 'nw-kpi__delta nw-kpi__delta--up' : 'nw-kpi__delta nw-kpi__delta--down';
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={Cls}>
      <Icon size={14} strokeWidth={2.4} />
      {`${Math.abs(delta).toFixed(1)}% vs prev 24h`}
    </span>
  );
}

function KpiCard({
  label,
  value,
  Icon,
  pendingMessage,
}: {
  readonly label: string;
  readonly value: KpiNumber | null;
  readonly Icon: LucideIcon;
  readonly pendingMessage?: string;
}) {
  return (
    <div className="nw-kpi">
      <div className="nw-kpi__head">
        <span className="nw-kpi__icon">
          <Icon size={18} strokeWidth={2} />
        </span>
        <span className="nw-kpi__label">{label}</span>
      </div>
      <div
        className={value ? 'nw-kpi__value' : 'nw-kpi__value nw-kpi__value--null'}
      >
        {formatValue(value)}
      </div>
      {value ? (
        <DeltaBadge delta={value.deltaPct} />
      ) : (
        <span className="nw-kpi__delta-note">
          {pendingMessage ?? 'wired up in a later slice'}
        </span>
      )}
    </div>
  );
}

export function Overview() {
  const q = useQuery({
    queryKey: queryKeys.overview(),
    queryFn: async () => {
      const res = await apiClient.get<OverviewResponse>('/owner/overview');
      return res.data;
    },
    refetchInterval: 60_000, // pulse refresh every minute
  });

  const data = q.data;

  return (
    <>
      <header className="nw-page-header">
        <h1 className="nw-page-header__title">Overview</h1>
        <p className="nw-page-header__subtitle">
          One-glance pulse for NewsWave — users, content, push delivery, errors and spend.
        </p>
      </header>

      <div className="nw-kpi-grid">
        <KpiCard
          label="DAU"
          value={data?.kpis.dau ?? null}
          Icon={Globe2}
          pendingMessage="PostHog wiring in a later slice"
        />
        <KpiCard
          label="MAU"
          value={data?.kpis.mau ?? null}
          Icon={Users}
          pendingMessage="PostHog wiring in a later slice"
        />
        <KpiCard
          label="Articles today"
          value={data?.kpis.articlesToday ?? null}
          Icon={Newspaper}
        />
        <KpiCard
          label="Push sent today"
          value={data?.kpis.pushSentToday ?? null}
          Icon={Bell}
        />
      </div>

      <div className="nw-kpi-grid">
        <KpiCard
          label="New users 24h"
          value={data?.secondary.newUsers24h ?? null}
          Icon={Users}
        />
        <KpiCard
          label="Active channels"
          value={data?.secondary.activeChannels ?? null}
          Icon={Newspaper}
        />
        <KpiCard
          label="Errors 24h"
          value={data?.secondary.errors24h ?? null}
          Icon={Activity}
          pendingMessage="Sentry wiring in a later slice"
        />
        <KpiCard
          label="MTD spend"
          value={data?.secondary.mtdSpend ?? null}
          Icon={Coins}
          pendingMessage="GCP Billing wiring in a later slice"
        />
      </div>

      <section className="nw-panel">
        <header className="nw-panel__header">
          <h2 className="nw-panel__title">Pulse — totals</h2>
        </header>
        <div className="nw-status-grid">
          <Totals
            label="Users"
            value={data?.totals.users}
            Icon={Users}
          />
          <Totals
            label="Articles"
            value={data?.totals.articles}
            Icon={Newspaper}
          />
          <Totals
            label="Channels"
            value={data?.totals.channels}
            Icon={Newspaper}
          />
          <Totals
            label="Active alerts"
            value={data?.totals.activeAlerts}
            Icon={AlertTriangle}
          />
          <Totals
            label="Devices"
            value={data?.totals.devices}
            Icon={Server}
          />
        </div>
      </section>

      {q.error && (
        <div
          style={{
            color: 'var(--error)',
            fontSize: 'var(--fs-sm)',
            marginTop: 'var(--sp-lg)',
          }}
        >
          Failed to load overview: {(q.error as Error).message}
        </div>
      )}
    </>
  );
}

function Totals({
  label,
  value,
  Icon,
}: {
  readonly label: string;
  readonly value: number | undefined;
  readonly Icon: LucideIcon;
}) {
  return (
    <div className="nw-status-tile">
      <div className="nw-status-tile__head">
        <Icon size={16} strokeWidth={2} color="var(--accent)" />
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 800,
          fontSize: 'var(--fs-2xl)',
          color: 'var(--text-primary)',
        }}
      >
        {value === undefined ? '—' : fmtInt.format(value)}
      </div>
    </div>
  );
}
